import React, { useEffect, useState } from 'react';
import { Button, Input, InputGroup, PanelGroup, Stack } from 'rsuite';
import { AxiosError } from 'axios';
import { Icon } from '@rsuite/icons';
import { MdOutlineContentCopy } from 'react-icons/md';
import ActionPanel from '@/components/ActionPanel';
import PageContent from '@/components/PageContent';
import FetchingSelect from '@/components/FetchingSelect';
import { AssignClubOwner, RunningClub } from '@/models/running-club.model';
import { BaseErrorModel, BaseResponseModel } from '@/models/core.model';
import { assignClubOwner, rotateClubJoinKey } from '@/services/running-club.service';
import { getUsersLookup } from '@/services/user.service';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import RunningClubCaptainProfileDrawer from '../running-club-captain-profile/running-club-captain-profile-drawer';

interface RunningClubActionsProps {
  formObject?: RunningClub;
  fetchData: () => void;
  // onKeyReverted?: (key: string) => void;
}

const RunningClubActions: React.FC<RunningClubActionsProps> = ({ formObject, fetchData }) => {
  const [ownerUserId, setSelectedUserId] = useState<number | undefined>(undefined);
  const [privateJoinKey, setPrivateJoinKey] = useState<string | undefined>(undefined);
  const [loadingAssignOwner, setLoadingAssignOwner] = useState(false);
  const [loadingRevertKey, setLoadingRevertKey] = useState(false);
  const [openCaptainProfileDrawer, setOpenCaptainProfileDrawer] = useState(false);

  useEffect(() => {
    setSelectedUserId(formObject?.ownerUserId);
    setPrivateJoinKey(formObject?.privateJoinKey);
  }, [formObject]);

  const handleAssignOwner = async () => {
    if (!formObject) return;

    if (!ownerUserId) {
      toastErrorMessage('Please select a user to assign as owner.');
      return;
    }

    setLoadingAssignOwner(true);
    try {
      const input: AssignClubOwner = {
        runningClubId: formObject.id,
        userId: ownerUserId,
      };
      const result: BaseResponseModel<null> = await assignClubOwner(input);
      if (result.success) {
        toastSuccessMessage('Owner assigned successfully.');
        fetchData();
      }
    } catch (err) {
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in assigning owner: ${errorModel?.errorMessage}`);
    } finally {
      setLoadingAssignOwner(false);
    }
  };

  const handleRevertPrivateKey = async () => {
    if (!formObject) return;

    setLoadingRevertKey(true);
    try {
      const result: BaseResponseModel<string> = await rotateClubJoinKey(formObject.id);
      if (result.success) {
        fetchData();
        toastSuccessMessage('Private key reverted successfully.');
      }
    } catch (err) {
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in reverting private key: ${errorModel?.errorMessage}`);
    } finally {
      setLoadingRevertKey(false);
    }
  };

  const handleCloseCaptainProfileDrawer = () => {
    setOpenCaptainProfileDrawer(false);
  };

  const copyPrivateKey = () => {
    if (!formObject?.privateJoinKey) return;
    navigator.clipboard.writeText(formObject.privateJoinKey);
    toastSuccessMessage('Private key copied.');
  };

  return (
    <PageContent>
      <PanelGroup>
        <ActionPanel
          title="Assign Owner"
          description="This action assigns a user as the owner of the club. Assigning a new owner transfers ownership; the current owner becomes a Captain."
          actionBar={
            <Stack spacing={6} justifyContent="flex-end">
              <FetchingSelect block
                fetchData={(text, page) => getUsersLookup(text, undefined, page)}
                placeholder="Select a user"
                value={ownerUserId}
                onChange={val => setSelectedUserId(val)}
                style={{ minWidth: 240 }}
              />
              <Button block
                appearance="ghost"
                onClick={handleAssignOwner}
                disabled={!formObject}
                loading={loadingAssignOwner}
              >
                Assign Owner
              </Button>
            </Stack>
          }
        />
        <ActionPanel
          title="Revert Private Key"
          description="This action reverts the private key of the club. Reverting invalidates every invite already shared; existing members are unaffected."
          actionBar={
            <Stack spacing={6} justifyContent="flex-end">
              <InputGroup style={{ minWidth: 220 }}>
                <Input value={privateJoinKey ?? ''} readOnly />
                <InputGroup.Button onClick={copyPrivateKey} disabled={!formObject}>
                  <Icon as={MdOutlineContentCopy} />
                </InputGroup.Button>
              </InputGroup>
              <Button block
                appearance="ghost"
                color="red"
                onClick={handleRevertPrivateKey}
                disabled={!formObject}
                loading={loadingRevertKey}
              >
                Revert
              </Button>
            </Stack>
          }
        />
        <ActionPanel
          title="Make Captain Profile"
          description="This action creates a captain profile for a user of the club: a picture, a title, a description and a display order. Profiles are listed on the Captain Profiles tab, where they can also be edited."
          actionBar={
            <Stack spacing={6} justifyContent="flex-end">
              <Button block
                appearance="ghost"
                onClick={() => setOpenCaptainProfileDrawer(true)}
                disabled={!formObject}
              >
                Make Captain Profile
              </Button>
            </Stack>
          }
        />
      </PanelGroup>

      <RunningClubCaptainProfileDrawer
        runningClubId={formObject?.id}
        open={openCaptainProfileDrawer}
        onClose={handleCloseCaptainProfileDrawer}
      />
    </PageContent>
  );
};

export default RunningClubActions;
